import React, { createContext, useState, useCallback, useContext } from 'react';
import { Notification, NotificationContextValue, NotificationProviderProps, TriggerNotificationPayload } from './types';
import NotificationContainer from './NotificationContainer';
import { ToastNotificationStyleEnum } from '../../components/atoms/ToastNotification/ToastNotification.types';

export const NotificationContext = createContext<NotificationContextValue>({} as NotificationContextValue);

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  position = 'top-right',
  offset = { x: 20, y: 20 }
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((
    message?: React.ReactNode,
    renderContent?: import('./types').NotificationRenderContent,
    duration: number = 0,
    style?: ToastNotificationStyleEnum,
    isDismissable: boolean = true,
    closeIcon?: React.ReactNode,
    leadingIcon?: React.ReactNode,
    trailingIcon?: React.ReactNode,
    isCloseButtonShown: boolean = true,
    position?: import('./types').NotificationPosition,
  ) => {
    const id = Date.now();
    const newNotification = {
      id,
      message,
      renderContent,
      duration,
      style,
      isDismissable,
      closeIcon,
      leadingIcon,
      trailingIcon,
      isCloseButtonShown,
      position,
    };
    setNotifications((prevNotifications) => [...prevNotifications, newNotification]);

    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  }, []);

  const removeNotification = useCallback((id: number) => {
    setNotifications((prevNotifications) => prevNotifications.filter(notification => notification.id !== id));
  }, []);

  const clear = useCallback(() => {
    setNotifications([]);
  }, []);

  const trigger = useCallback((payload: TriggerNotificationPayload) => {
    const {
      message,
      renderContent,
      duration,
      style,
      isDismissable,
      closeIcon,
      leadingIcon,
      trailingIcon,
      isCloseButtonShown,
      position,
    } = payload;
    addNotification(
      message,
      renderContent,
      duration ?? 0,
      style ?? 'Neutral',
      isDismissable,
      closeIcon,
      leadingIcon,
      trailingIcon,
      isCloseButtonShown,
      position,
    );
  }, [addNotification]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      clear,
      trigger,
      removeNotification,
      position,
      offset,
    }}>
      <NotificationContainer position={position} offset={offset} />
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  return context;
};